import { useEffect, useRef, useState, type CSSProperties } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { getErrorMessage } from '../../utils/errors';
import { formatTime } from '../../utils/time';

import classes from './Timer.module.scss';
import { dateDiffInSeconds } from './utils';

export type TimerProps = {
  endDate: Date;
  prefix?: string;
  className?: string;
  style?: CSSProperties;
  onClick?: () => void;
  onTimerEnd?: () => void;
  dataTestId?: string;
};

export const Timer = ({ className, endDate, prefix, onClick, onTimerEnd, ...otherProps }: TimerProps) => {
  const [seconds, setSeconds] = useState(dateDiffInSeconds(endDate || new Date()));
  const { t } = useTranslation();

  const onTimerEndRef = useRef(onTimerEnd);
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    try {
      if (!endDate) return;

      interval = setInterval(() => {
        const next = dateDiffInSeconds(endDate);
        if (next <= 0) {
          onTimerEndRef.current?.();
          clearInterval(interval!);
        }
        setSeconds(next);
      }, 1000);
    } catch (e) {
      const errorMessage = getErrorMessage(e);
      console.error(`Timer error: ${errorMessage}`);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [endDate]);

  const formatted = formatTime(seconds, 's', t);

  return (
    <div
      className={classNames(classes.timer, className)}
      {...otherProps}
      data-testid={otherProps.dataTestId}
      style={{
        ...otherProps?.style,
      }}
      onClick={onClick}
    >
      {prefix} {formatted}
    </div>
  );
};
